import { z } from 'zod';

// Request timeout (30s) for the embedded authorization calls.
const REQUEST_TIMEOUT_MS = 30_000;

/** Maximum number of resume advances to follow before giving up. */
export const MAX_RESUME_ITERATIONS = 10;

/**
 * HTTP-level failure from the embedded authorization endpoint. Lets callers
 * branch on the status code (e.g. 503 -> backend downtime).
 */
export class EmbeddedFlowHTTPError extends Error {
  readonly status: number;
  readonly statusText: string;

  constructor(status: number, statusText: string) {
    super(`Embedded flow HTTP error: ${status} ${statusText}`);
    this.name = 'EmbeddedFlowHTTPError';
    this.status = status;
    this.statusText = statusText;
  }
}

/**
 * Parameters for the embedded authorization start request. `intentId` is the
 * only optional field — the backend accepts an optional `intent_id`.
 */
export type EmbeddedFlowStartParams = {
  baseUrl: string;
  clientId: string;
  scope: string;
  state: string;
  codeChallenge: string;
  webhookUri: string;
  intentId?: string;
};

/** Success body of `GET /authorization/embedded/request`. */
const startSuccessSchema = z.object({
  authorization: z.string(),
  interaction: z.string(),
  oauth_interaction_start_uri: z.string(),
  oauth_interaction_finalize_uri: z.string(),
  oauth_authorization_resume_uri: z.string(),
});

/** Error body of `GET /authorization/embedded/request`. */
const startErrorSchema = z.object({
  error: z.string(),
  error_description: z.string(),
});

/**
 * Outcome of the embedded authorization start call. On success it carries the
 * first interaction's identifiers and the three per-interaction URIs; on a
 * protocol error it carries the OAuth error fields.
 */
export type EmbeddedFlowStartResult =
  | {
      status: 'success';
      authorization: string;
      interaction: string;
      startUri: string;
      finalizeUri: string;
      resumeUri: string;
    }
  | { status: 'error'; error: string; errorDescription: string };

function buildRequestUrl(params: EmbeddedFlowStartParams): string {
  const url = new URL(`${params.baseUrl}/authorization/embedded/request`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('scope', params.scope);
  url.searchParams.set('state', params.state);
  url.searchParams.set('code_challenge', params.codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('webhook_uri', params.webhookUri);
  if (params.intentId) url.searchParams.set('intent_id', params.intentId);
  return url.toString();
}

/**
 * driveEmbeddedFlow — performs ONLY the embedded authorization start:
 *   GET {baseUrl}/authorization/embedded/request?response_type=code&client_id=…
 *       &scope=…&state=…&code_challenge=…&code_challenge_method=S256[&intent_id=…]
 *
 * The start response returns the first interaction directly. Advancing the flow
 * afterwards is driven per-interaction via the resume helper.
 */
export async function driveEmbeddedFlow(
  params: EmbeddedFlowStartParams,
  signal: AbortSignal,
): Promise<EmbeddedFlowStartResult> {
  const combinedSignal = AbortSignal.any([
    signal,
    AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  ]);

  const response = await fetch(buildRequestUrl(params), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-cache',
    signal: combinedSignal,
  });

  const body = await response.json();

  const errorResult = startErrorSchema.safeParse(body);
  if (errorResult.success) {
    return {
      status: 'error',
      error: errorResult.data.error,
      errorDescription: errorResult.data.error_description,
    };
  }

  if (!response.ok) {
    throw new EmbeddedFlowHTTPError(response.status, response.statusText);
  }

  const parsed = startSuccessSchema.safeParse(body);
  if (!parsed.success) {
    throw new Error(
      `[Ronan] Unexpected embedded/request response body: ${JSON.stringify(body)} \u2014 ${JSON.stringify(parsed.error.issues)}`,
    );
  }
  return {
    status: 'success',
    authorization: parsed.data.authorization,
    interaction: parsed.data.interaction,
    startUri: parsed.data.oauth_interaction_start_uri,
    finalizeUri: parsed.data.oauth_interaction_finalize_uri,
    resumeUri: parsed.data.oauth_authorization_resume_uri,
  };
}

/** InteractionSelected body of `GET /authorization/embedded/resume`. */
const resumeSelectedSchema = z.object({
  authorization: z.string(),
  interaction: z.string(),
  oauth_interaction_start_uri: z.string(),
  oauth_interaction_finalize_uri: z.string(),
  oauth_authorization_resume_uri: z.string(),
});

/** Error body of `GET /authorization/embedded/resume`. */
const resumeErrorSchema = z.object({
  error: z.string(),
  error_description: z.string(),
});

/** CollectionCompleted body of `GET /authorization/embedded/resume`. */
const resumeCompletedSchema = z.object({
  state: z.string(),
});

/**
 * Outcome of advancing an embedded authorization via its resume URI.
 */
export type EmbeddedResumeResult =
  | {
      status: 'interactionSelected';
      authorization: string;
      interaction: string;
      startUri: string;
      finalizeUri: string;
      resumeUri: string;
    }
  | { status: 'completed'; state: string }
  | { status: 'error'; error: string; errorDescription: string };

/**
 * driveEmbeddedResume — advances an embedded authorization by fetching its
 * resume URI (in embedded mode the resume endpoint must be fetched, not
 * navigated to) and mapping the JSON outcome to a discriminated union:
 * InteractionSelected | CollectionCompleted | Error.
 */
export async function driveEmbeddedResume(
  resumeUri: string,
  signal: AbortSignal,
): Promise<EmbeddedResumeResult> {
  const combinedSignal = AbortSignal.any([
    signal,
    AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  ]);

  const response = await fetch(resumeUri, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-cache',
    signal: combinedSignal,
  });

  const body = await response.json();

  const selected = resumeSelectedSchema.safeParse(body);
  if (selected.success) {
    return {
      status: 'interactionSelected',
      authorization: selected.data.authorization,
      interaction: selected.data.interaction,
      startUri: selected.data.oauth_interaction_start_uri,
      finalizeUri: selected.data.oauth_interaction_finalize_uri,
      resumeUri: selected.data.oauth_authorization_resume_uri,
    };
  }

  const errorResult = resumeErrorSchema.safeParse(body);
  if (errorResult.success) {
    return {
      status: 'error',
      error: errorResult.data.error,
      errorDescription: errorResult.data.error_description,
    };
  }

  const completed = resumeCompletedSchema.safeParse(body);
  if (completed.success) {
    return { status: 'completed', state: completed.data.state };
  }

  throw new Error(
    `[Ronan] Unexpected embedded/resume response body (HTTP ${response.status} ${response.statusText}): ${JSON.stringify(body)}`,
  );
}
